import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";
import { queryKeys } from "../queryKeys";
import {
    getBenchmarkInputsAsync,
    getBenchmarkInputByIdAsync,
    createBenchmarkInputAsync,
    updateBenchmarkInputAsync,
    deleteBenchmarkInputAsync,
    getBenchmarkOutputsAsync,
    getBenchmarkOutputByIdAsync,
    getBenchmarkOutputsByInputIdAsync,
    createBenchmarkOutputAsync,
    updateBenchmarkOutputAsync,
    deleteBenchmarkOutputAsync,
    getBenchmarkReferencesAsync,
    getBenchmarkReferenceByIdAsync,
    createBenchmarkReferenceAsync,
    updateBenchmarkReferenceAsync,
    deleteBenchmarkReferenceAsync,
    CreateOrUpdateBenchmarkInput,
    CreateOrUpdateBenchmarkOutput,
    CreateOrUpdateBenchmarkReference,
    getBenchmarkInputByClientIdAsync,
} from "../benchmark/benchmarkApi";

// Benchmark Input Queries
export function useGetBenchmarkInputs() {
    return useQuery({ ...queryKeys.benchmarkInputs.all, queryFn: getBenchmarkInputsAsync });
}

export function useGetBenchmarkInputsByClientId(clientId: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.benchmarkInputs.byClientId(clientId || ""),
        queryFn: async () => {
            const benchmarkInput = await getBenchmarkInputByClientIdAsync(clientId || "")
                .then(benchmarkInput => benchmarkInput)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the benchmark input information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return benchmarkInput;
        },
        enabled: !!clientId,
    });
}

export function useGetBenchmarkInput(id: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.benchmarkInputs.byId(id || ""),
        queryFn: async () => {
            const benchmarkInput = await getBenchmarkInputByIdAsync(id || "")
                .then(benchmarkInput => benchmarkInput)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the benchmark input information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return benchmarkInput;
        },
        enabled: !!id,
    });
}

export function useCreateBenchmarkInput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { benchmarkInput: CreateOrUpdateBenchmarkInput; file: FileContainer }) => {
            return await createBenchmarkInputAsync(args.benchmarkInput, args.file);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.benchmarkInputs.all);
            toast({
                title: "Created",
                description: "Benchmark input created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create benchmark input"),
    });
}

export function useUpdateBenchmarkInput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { data: CreateOrUpdateBenchmarkInput; file: FileContainer | undefined }) => {
            return await updateBenchmarkInputAsync(args.data, args.file);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.benchmarkInputs.all);
            toast({
                title: "Updated",
                description: "Benchmark input updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update benchmark input"),
    });
}

export function useDeleteBenchmarkInput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (id: string) => {
            return await deleteBenchmarkInputAsync(id);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkInputs.all);
            await queryClient.invalidateQueries(queryKeys.benchmarkOutputs.all);
            toast({
                title: "Deleted",
                description: "Benchmark input deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete benchmark input"),
    });
}

// Benchmark Output Queries
export function useGetBenchmarkOutputs() {
    return useQuery({ ...queryKeys.benchmarkOutputs.all, queryFn: getBenchmarkOutputsAsync });
}

export function useGetBenchmarkOutput(id: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.benchmarkOutputs.byId(id || ""),
        queryFn: async () => {
            const benchmarkOutput = await getBenchmarkOutputByIdAsync(id || "")
                .then(benchmarkOutput => benchmarkOutput)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the benchmark output information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return benchmarkOutput;
        },
        enabled: !!id,
    });
}

export function useGetBenchmarkOutputsByInputId(inputId: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.benchmarkOutputs.byInputId(inputId || ""),
        queryFn: async () => {
            const benchmarkOutputs = await getBenchmarkOutputsByInputIdAsync(inputId || "")
                .then(benchmarkOutputs => benchmarkOutputs)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the benchmark outputs for this input.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return benchmarkOutputs;
        },
        enabled: !!inputId,
    });
}

export function useCreateBenchmarkOutput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: CreateOrUpdateBenchmarkOutput) => {
            return await createBenchmarkOutputAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkOutputs.all);
            toast({
                title: "Created",
                description: "Benchmark output created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create benchmark output"),
    });
}

export function useUpdateBenchmarkOutput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { id: string; data: CreateOrUpdateBenchmarkOutput }) => {
            return await updateBenchmarkOutputAsync(args.id, args.data);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkOutputs.all);
            toast({
                title: "Updated",
                description: "Benchmark output updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update benchmark output"),
    });
}

export function useDeleteBenchmarkOutput() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (id: string) => {
            return await deleteBenchmarkOutputAsync(id);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkOutputs.all);
            toast({
                title: "Deleted",
                description: "Benchmark output deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete benchmark output"),
    });
}

// Benchmark Reference Queries
export function useGetBenchmarkReferences() {
    return useQuery({ ...queryKeys.benchmarkReferences.all, queryFn: getBenchmarkReferencesAsync });
}

export function useGetBenchmarkReference(id: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.benchmarkReferences.byId(id || ""),
        queryFn: async () => {
            const benchmarkReference = await getBenchmarkReferenceByIdAsync(id || "")
                .then(benchmarkReference => benchmarkReference)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the benchmark reference information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return benchmarkReference;
        },
        enabled: !!id,
    });
}

export function useCreateBenchmarkReference() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { benchmarkReference: CreateOrUpdateBenchmarkReference; file: FileContainer | undefined }) => {
            return await createBenchmarkReferenceAsync(args.benchmarkReference, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkReferences.all);
            toast({
                title: "Created",
                description: "Benchmark reference created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create benchmark reference"),
    });
}

export function useUpdateBenchmarkReference() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { data: CreateOrUpdateBenchmarkReference; file: FileContainer | undefined }) => {
            return await updateBenchmarkReferenceAsync(args.data, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkReferences.all);
            toast({
                title: "Updated",
                description: "Benchmark reference updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update benchmark reference"),
    });
}

export function useDeleteBenchmarkReference() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (id: string) => {
            return await deleteBenchmarkReferenceAsync(id);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.benchmarkReferences.all);
            toast({
                title: "Deleted",
                description: "Benchmark reference deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete benchmark reference"),
    });
}
